import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import usePageTitle from '../../hooks/UsePageTitle';

const LegalRegulatoryCompliance = () => {
  usePageTitle('Legal & Regulatory Compliance');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">Legal & Regulatory Compliance</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/legal1.jpg'
            alt="Legal & Regulatory Compliance" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              India's regulatory environment is dynamic, dense, and subject to rapid reforms — from Companies Act 
              and FEMA to SEBI, RBI, and labour laws. Staying compliant is not just about avoiding penalties but 
              ensuring sustainable growth, investor confidence, and business continuity.
            </p>
            <p className="text-lg line-height-3 mt-3">
              At SnP Finvisors, our Legal & Regulatory Compliance services help businesses navigate this maze with 
              confidence. Whether you're a startup, SME, listed company, or an international entity operating in 
              India, we help you stay updated, audit-ready, and risk-free.
            </p>
          </Card>
        </div>

        {/* Challenges Section */}
        <div className="col-12 mt-5">
          <h2>Key Challenges Faced by Businesses</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <ul className="text-xl line-height-3 ml-4">
                <li className="mb-2">Frequent updates to corporate, tax, and foreign exchange laws</li>
                <li className="mb-2">Complexities in entity structuring, filings, and disclosures</li>
                <li className="mb-2">Need for compliance across multiple regulators: MCA, RBI, SEBI, GST, etc.</li>
                <li>High cost of non-compliance: penalties, reputational damage, or even license cancellations</li>
              </ul>
              <p className="text-xl line-height-3 mt-3 font-semibold text-primary">
                We act as your compliance backbone — proactive, preventive, and strategic.
              </p>
            </div>
            <img 
              src='/images/legal2.jpg' 
              alt="Compliance Challenges" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Services Section */}
        <div className="col-12 mt-5">
          <h2>Scope of Our Legal & Regulatory Compliance Services</h2>
          <Divider />
          
          <div className="grid">
            {/* Service 1 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="1. Corporate Law Compliance" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Incorporation of companies, LLPs, subsidiaries, and JVs</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Maintenance of statutory registers, minutes, and filings</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>ROC filings: MGT-7, AOC-4, DIR-3 KYC, and event-based forms</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Compliance with Companies Act, 2013: board processes, annual filings, shareholder meetings</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Conversion of company structures, name changes, capital restructuring</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 2 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="2. FEMA & RBI Advisory" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Compliance with FEMA for inbound/outbound investments</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>FDI & ODI advisory, reporting (FC-GPR, FC-TRS, ODI Forms)</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Assistance in RBI compounding applications</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Structuring and documentation of ECBs, share swaps, and convertible instruments</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 3 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="3. SEBI & Listing Compliance (for listed clients)" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Periodic disclosures under LODR regulations</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Secretarial audits, related party transactions monitoring</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Compliance with insider trading and takeover regulations</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Support for investor grievance redressal mechanisms</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 4 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="4. Legal Drafting & Vetting" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Drafting of shareholder agreements, term sheets, NDAs, and employment contracts</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Vetting of vendor agreements, lease deeds, and MOUs</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Legal support during fundraising, due diligence, and restructuring</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 5 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="5. Regulatory Representation & Licenses" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Assistance in dealing with regulatory inquiries, show-cause notices, or inspections</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Filing and follow-up for business registrations, licenses, and amendments (FSSAI, MSME, IEC, etc.)</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Support in responding to MCA, RBI, or SEBI correspondences</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <h2>Your Trusted Compliance Partner</h2>
              <Divider />
              <p className="text-xl line-height-3">
                At SnP Finvisors, we don't just keep you compliant — we help you turn regulatory compliance into 
                a competitive advantage. Our proactive approach ensures you stay ahead of regulatory changes, 
                maintain good corporate governance, and focus on your core business with peace of mind.
              </p>
              <p className="text-xl line-height-3 mt-3 font-semibold">
                In today's complex regulatory landscape, having the right compliance partner makes all the difference.
              </p>
            </div>
            <img 
              src='/images/legal3.jpg'  
              alt="Compliance Partnership" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalRegulatoryCompliance;